import { Box } from "lucide-react";
import { useEffect, useState } from "react";

import { PcbCard } from "../../components/PcbCard";

type MiningMode = "auto" | "manual" | "interval";

interface ChainStateHeaderProps {
  simulatedTimestamp: number | null;
  blockNumber: bigint | undefined;
  blockPulse: boolean;
  miningMode: MiningMode;
  onBlockClick?: () => void;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDivergence(seconds: number): string {
  const abs = Math.abs(seconds);
  if (abs < 60) return `${String(abs)}s`;
  if (abs < 3600) {
    const m = Math.floor(abs / 60);
    const s = abs % 60;
    return s > 0 ? `${String(m)}m ${String(s)}s` : `${String(m)}m`;
  }
  if (abs < 86400) {
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    return m > 0 ? `${String(h)}h ${String(m)}m` : `${String(h)}h`;
  }
  const d = Math.floor(abs / 86400);
  const h = Math.floor((abs % 86400) / 3600);
  return h > 0 ? `${String(d)}d ${String(h)}h` : `${String(d)}d`;
}

export function classifyDivergence(seconds: number): {
  label: string;
  color: string;
  variant: "success" | "warning" | "error" | "info";
} {
  if (seconds > 0)
    return {
      label: "Ahead of real time",
      color: "text-purple-400",
      variant: "info",
    };
  const abs = Math.abs(seconds);
  if (abs < 10)
    return {
      label: "In sync",
      color: "text-[var(--color-success)]",
      variant: "success",
    };
  if (abs < 60)
    return {
      label: "Slightly behind",
      color: "text-[var(--color-success)]",
      variant: "success",
    };
  if (abs < 600)
    return {
      label: "Behind",
      color: "text-amber-400",
      variant: "warning",
    };
  if (abs < 3600)
    return {
      label: "Significantly behind",
      color: "text-orange-400",
      variant: "warning",
    };
  return {
    label: "Far behind",
    color: "text-[var(--color-error)]",
    variant: "error",
  };
}

export { formatDivergence };

export function ChainStateHeader({
  simulatedTimestamp,
  blockNumber,
  blockPulse,
  miningMode,
  onBlockClick,
}: ChainStateHeaderProps) {
  const [timeDivergence, setTimeDivergence] = useState<number | null>(null);
  const [realNow, setRealNow] = useState<Date>(new Date());

  useEffect(() => {
    if (simulatedTimestamp === null) return;
    const update = () => {
      const now = new Date();
      setRealNow(now);
      setTimeDivergence(simulatedTimestamp - Math.floor(now.getTime() / 1000));
    };
    update();
    const id = setInterval(update, 1000);
    return () => {
      clearInterval(id);
    };
  }, [simulatedTimestamp]);

  const simDate =
    simulatedTimestamp !== null ? new Date(simulatedTimestamp * 1000) : null;

  const divergence =
    timeDivergence !== null ? classifyDivergence(timeDivergence) : null;

  const miningDotColor =
    miningMode === "auto"
      ? "bg-[var(--color-success)]"
      : miningMode === "interval"
        ? "bg-amber-400"
        : "bg-blue-400";

  return (
    <PcbCard className="col-span-full">
      <div className="flex flex-wrap items-center justify-between gap-6">
        {/* Left: Date/time */}
        <div className="space-y-2">
          {simDate ? (
            <>
              <p className="font-serif text-2xl tracking-tight text-[var(--color-text)]">
                {simDate.toLocaleDateString("en-US", {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </p>

              {/* Time divergence */}
              {timeDivergence !== null && divergence && (
                <div className="flex items-center gap-3">
                  <span className={`font-mono text-xs ${divergence.color}`}>
                    {formatTime(simDate)}
                  </span>
                  <span className="text-[10px] text-[var(--color-text-muted)]">
                    {timeDivergence >= 0 ? "+" : ""}
                    {formatDivergence(timeDivergence)} vs real (
                    {formatTime(realNow)})
                  </span>
                </div>
              )}

              {/* Status pills */}
              <div className="flex flex-wrap gap-3 pt-1">
                <span className="flex items-center gap-1.5">
                  <span
                    className={`inline-block h-1.5 w-1.5 rounded-full ${miningDotColor}`}
                  />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    {miningMode} mining
                  </span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="bg-[var(--color-text-muted)]/40 inline-block h-1.5 w-1.5 rounded-full" />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    10 accounts
                  </span>
                </span>
              </div>
            </>
          ) : (
            <p className="text-xl font-semibold text-[var(--color-text-muted)]">
              Loading...
            </p>
          )}
        </div>

        {/* Right: Block number — hero treatment */}
        <div className="text-right">
          {onBlockClick ? (
            <button
              type="button"
              onClick={onBlockClick}
              className={`stat-number cursor-pointer text-4xl hover:underline ${blockPulse ? "animate-block-pulse" : ""}`}
              title="View transaction log"
            >
              <Box
                size={18}
                className="text-[var(--color-gold)]/40 mr-1.5 inline-block align-middle"
              />
              {blockNumber?.toString() ?? "..."}
            </button>
          ) : (
            <p
              className={`stat-number text-4xl ${blockPulse ? "animate-block-pulse" : ""}`}
            >
              <Box
                size={18}
                className="text-[var(--color-gold)]/40 mr-1.5 inline-block align-middle"
              />
              {blockNumber?.toString() ?? "..."}
            </p>
          )}
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
            Block Height
          </p>
        </div>
      </div>
    </PcbCard>
  );
}
